import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Layers, Calculator, ChevronRight, ChevronLeft } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "./ui/sonner";

/**
 * First-login micro-tour. Shown on the dashboard ONLY when the authenticated
 * user has `has_seen_onboarding === false` (strict equality — legacy users
 * without the field are treated as already onboarded).
 *
 * 3 steps walking through the redesigned Add Asset flow:
 *   1. Welcome — "Add your first property in 3 steps"
 *   2. Smart sections preview (Property / Costs / Income)
 *   3. Auto-calculate + Override pattern preview
 *
 * Both "Start adding" and "Skip" mark `/auth/onboarding/complete` so the
 * tour never replays for this account.
 */
export default function OnboardingTour({ onStartAdding }) {
  const { user, setUser } = useAuth();
  const [step, setStep] = useState(0);
  const [closing, setClosing] = useState(false);

  // Strict equality on purpose: legacy users created before this feature have
  // the field missing/undefined, so they're treated as already onboarded and
  // never see the tour. Only newly-registered users get an explicit `false`.
  const shouldShow =
    !!user &&
    user !== false &&
    user.has_seen_onboarding === false &&
    user.has_accepted_legal === true; // wait until legal modal is dismissed

  const markComplete = async () => {
    // Optimistic local update so the tour disappears immediately.
    setUser?.((curr) =>
      curr && curr !== false ? { ...curr, has_seen_onboarding: true } : curr
    );
    try {
      await api.post("/auth/onboarding/complete");
    } catch (err) {
      // Non-fatal — user just sees the tour again next session at worst.
      toast.error("Could not save onboarding status", {
        description: err.response?.data?.detail || "We'll try again next time.",
      });
    }
  };

  const handleSkip = async () => {
    setClosing(true);
    await markComplete();
  };

  const handleStartAdding = async () => {
    setClosing(true);
    await markComplete();
    onStartAdding?.();
  };

  const handleNext = () => setStep((s) => Math.min(s + 1, 2));
  const handleBack = () => setStep((s) => Math.max(s - 1, 0));

  const steps = [
    {
      icon: Sparkles,
      kicker: "Welcome to EQUIA",
      title: "Your international portfolio. One calm place.",
      body: (
        <>
          <p className="text-white/90 text-sm leading-relaxed">
            You&apos;ve invested across borders. Managing it shouldn&apos;t
            mean spreadsheets, email threads and FX anxiety.
            EQUIA replaces the chaos — starting with your
            first property.
          </p>
        </>
      ),
    },
    {
      icon: Layers,
      kicker: "Smart Entry",
      title: "Add a property in under a minute.",
      body: (
        <>
          <p className="text-white/90 text-sm leading-relaxed">
            Three focused steps: property details, closing costs
            calculated automatically for <span className="text-[#D4AF37]">Dubai</span>, <span className="text-[#D4AF37]">North Cyprus</span> and <span className="text-[#D4AF37]">Germany</span>,
            income and operations. Have your purchase
            contract? Upload it — AI fills the form for you.
          </p>
          <ul className="space-y-2.5 mt-2">
            {["Property — name, location, price, date", "Acquisition Costs — auto-calculated for you", "Income & Operations — rent, financing, running costs"].map((label, i) => (
              <li key={i} className="flex items-center gap-3" data-testid={`tour-step-bullet-${i + 1}`}>
                <span className="w-6 h-6 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center text-[10px] font-mono text-[#D4AF37]">
                  {i + 1}
                </span>
                <span className="text-white/75 text-sm">{label}</span>
              </li>
            ))}
          </ul>
        </>
      ),
    },
    {
      icon: Calculator,
      kicker: "Jurisdiction-Aware Calculations",
      title: "Every closing cost. Automatically.",
      body: (
        <>
          <p className="text-white/90 text-sm leading-relaxed">
            Property transfer taxes, notary fees and registration
            costs — all estimated from your purchase price the
            moment you select a location. Override any figure
            if your exact numbers differ.
          </p>
          <div className="border border-white/[0.10] bg-white/[0.02] p-3 space-y-2 mt-2">
            <p className="text-[9px] tracking-[0.15em] uppercase text-white/55">
              Germany · Closing Costs preview
            </p>
            <div className="flex items-center justify-between text-xs">
              <span className="text-white/80">Purchase Price</span>
              <span className="font-mono text-white">€ 350,000</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-white/80">Grunderwerbsteuer (3.5%)</span>
              <div className="flex items-center gap-3">
                <span className="font-mono text-white/80">€ 12,250</span>
                <span className="text-[9px] tracking-[0.12em] uppercase text-[#D4AF37]/70">
                  Override
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-white/80">Notar- & Gerichtskosten (~1.5%)</span>
              <div className="flex items-center gap-3">
                <span className="font-mono text-white/80">€ 5,250</span>
                <span className="text-[9px] tracking-[0.12em] uppercase text-[#D4AF37]/70">
                  Override
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-[#D4AF37]/20">
              <span className="text-white text-xs font-medium">Total Investment</span>
              <span className="font-mono text-sm text-[#D4AF37] font-medium">€ 367,500</span>
            </div>
          </div>
        </>
      ),
    },
  ];

  const cur = steps[step];
  const Icon = cur.icon;
  const isLast = step === steps.length - 1;

  return (
    <AnimatePresence>
      {shouldShow && !closing && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[150] flex items-center justify-center p-4"
          data-testid="onboarding-tour"
        >
          <div className="absolute inset-0 bg-black/85 backdrop-blur-md" />

          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.25, ease: [0.2, 0.8, 0.2, 1] }}
            className="relative w-full max-w-lg bg-[#0A0A0A] border border-white/[0.12] shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="onboarding-title"
          >
            {/* Header */}
            <div className="flex items-start gap-4 px-6 sm:px-7 pt-7 pb-5 border-b border-white/[0.10]">
              <div className="w-10 h-10 flex items-center justify-center bg-[#D4AF37]/[0.06] border border-[#D4AF37]/25 flex-shrink-0">
                <Icon className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] tracking-[0.2em] uppercase text-white/65 mb-1">
                  {cur.kicker}
                </p>
                <h2
                  id="onboarding-title"
                  className="font-serif text-xl sm:text-2xl text-white leading-tight"
                  data-testid="tour-title"
                >
                  {cur.title}
                </h2>
              </div>
              <button
                onClick={handleSkip}
                data-testid="tour-skip-x"
                className="text-white/65 hover:text-white transition-colors flex-shrink-0"
                aria-label="Skip tour"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 sm:px-7 py-6 space-y-3" data-testid="tour-body">
              {cur.body}
            </div>

            {/* Step dots */}
            <div className="flex items-center justify-center gap-2 px-6 sm:px-7 pb-2">
              {steps.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`tour-dot-${i}`}
                  onClick={() => setStep(i)}
                  aria-label={`Go to step ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${
                    i === step ? "w-6 bg-[#D4AF37]" : "w-1.5 bg-white/15 hover:bg-white/30"
                  }`}
                />
              ))}
            </div>

            {/* Footer */}
            <div className="px-6 sm:px-7 py-5 border-t border-white/[0.10] bg-black/30 flex items-center gap-3">
              {step > 0 ? (
                <button
                  type="button"
                  onClick={handleBack}
                  data-testid="tour-back-btn"
                  className="text-[11px] tracking-[0.15em] uppercase text-white/80 hover:text-white px-3 py-2 transition-colors flex items-center gap-1"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                  Back
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSkip}
                  data-testid="tour-skip-btn"
                  className="text-[11px] tracking-[0.15em] uppercase text-white/60 hover:text-white/90 px-3 py-2 transition-colors"
                >
                  Skip for now
                </button>
              )}

              <div className="flex-1" />

              {!isLast ? (
                <button
                  type="button"
                  onClick={handleNext}
                  data-testid="tour-next-btn"
                  className="bg-white/[0.06] hover:bg-white/[0.1] border border-white/15 text-white px-5 py-2.5 text-xs tracking-[0.15em] uppercase transition-colors flex items-center gap-2"
                >
                  Next
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleStartAdding}
                  data-testid="tour-start-adding-btn"
                  className="bg-[#D4AF37] hover:bg-[#B5952F] text-black px-5 py-2.5 text-xs tracking-[0.15em] uppercase font-medium transition-colors flex items-center gap-2"
                >
                  Add my first property
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
